import { useMemo } from 'react';

import { Drawer } from '@client:common/drawer';

import { SegmentedProgressBar } from '../segmented-progress-bar/SegmentedProgressBar';
import { Page } from '../../models/FlowChart';
import './TransitionTrail.light.scss';
import './TransitionTrail.dark.scss';

interface Props {
  visitedPageTrail: Page[];
  currentPage: Page;
  onMakePageCurrent: (page: Page) => void;
  isMobile: boolean;
  currentMobilePage: Page;
}

export function TransitionTrail(props: Props) {
  const { isMobile, visitedPageTrail, onMakePageCurrent, currentPage, currentMobilePage: __currentMobilePage__ } = props;

  const progressDecimal = useMemo(() => {
    if (!currentPage || !visitedPageTrail || visitedPageTrail.length === 0) {
      return 0;
    }

    if (currentPage.level === 0) {
      return currentPage.number;
    }

    const currentPageIndex = visitedPageTrail.findIndex(page => page.UUID === currentPage.UUID);
    if (currentPageIndex === -1) {
      return currentPage.number;
    }

    let parentStep: Page | null = null;
    for (let i = currentPageIndex; i >= 0; i--) {
      if (visitedPageTrail[i] && visitedPageTrail[i].level === 0) {
        parentStep = visitedPageTrail[i];
        break;
      }
    }

    if (!parentStep) {
      return currentPage.number;
    }

    const substeps = visitedPageTrail.filter(
      page => page.level > 0 &&
        (() => {
          for (let i = visitedPageTrail.indexOf(page); i >= 0; i--) {
            if (visitedPageTrail[i].level === 0) {
              return visitedPageTrail[i].UUID === parentStep.UUID;
            }
          }
          return false;
        })()
    );

    const sortedSubsteps = substeps.sort((a, b) =>
      visitedPageTrail.indexOf(a) - visitedPageTrail.indexOf(b)
    );

    const currentSubstepIndex = sortedSubsteps.findIndex(page => page.UUID === currentPage.UUID);

    if (currentSubstepIndex === -1 || sortedSubsteps.length === 0) {
      return parentStep.number + 0.5;
    }

    const fractionPerStep = 1 / (sortedSubsteps.length + 1);
    return parentStep.number + fractionPerStep * (currentSubstepIndex + 1);
  }, [currentPage, visitedPageTrail]);

  const totalSteps = useMemo(() =>
    visitedPageTrail
      .filter(page => page.level === 0)
      .reduce((max, page) => Math.max(max, page.number), 0),
    [visitedPageTrail]
  );

  interface Node {
    page: Page;
    children: Node[];
  }

  const buildTree = (pages: Page[]): Node[] => {
    const root: Node = { page: null, children: [] } as any;
    const stack: Node[] = [root];
    for (const page of pages) {
      const node: Node = { page, children: [] };
      while (stack.length > page.level + 1) {
        stack.pop();
      }
      stack[stack.length - 1].children.push(node);
      stack.push(node);
    }
    return root.children;
  };

  const TrailNodes = ({ nodes, level, onMakePageCurrent, currentPage }: { nodes: Node[]; level: number; onMakePageCurrent: (page: Page) => void; currentPage: Page }) => (
    <ul className={`transition-trail__sublist level--${level}`}>
      {nodes.map(node => (
        <li
          key={node.page.label}
          className={`transition-trail__list-item level--${node.page.level}${currentPage.UUID === node.page.UUID ? ' active' : ''}`}
          onClick={() => onMakePageCurrent(node.page)}
        >
          {node.page.label}
          {node.children.length > 0 && <TrailNodes nodes={node.children} level={level + 1} onMakePageCurrent={onMakePageCurrent} currentPage={currentPage} />}
        </li>
      ))}
    </ul>
  );

  if (isMobile) {
    return (
      <div className="transition-trail transition-trail--mobile">
        <h2 className="transition-trail__mobile-heading">Steps</h2>
        <SegmentedProgressBar
          currentProgress={progressDecimal}
          totalSteps={totalSteps}
        />
      </div>
    );
  }

  return (
    <Drawer
      panelType='non-collapsible'
      placement='left'
      className='transition-trail'
    >
      <h2>{`Progress Steps`}</h2>
      <TrailNodes
        nodes={buildTree(visitedPageTrail)}
        level={0}
        onMakePageCurrent={onMakePageCurrent}
        currentPage={currentPage}
      />
    </Drawer>
  );
}
